import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { setJourneyToken } from "@/lib/journeyToken";
import { useNavigate } from "react-router-dom";
import { trackConsent } from "@/lib/apis.js";
import { CheckCircle2Icon, ChevronLeftIcon } from "lucide-react";
import { CONSENT_MESSAGES } from "@/lib/data.js";
import { useJourneyLender } from "@/lib/journeyLender";
import {
  getDataB2B,
  formatDate,
  updateFunction,
  trackBtnClick,
  fetchData,
  formatINR,
  updateFunctionB2B,
} from "@/lib/helper";
import {
  partnerNavigationRules,
  partnerData,
  getMaintenanceInfo,
  ADD_MAINTENANCE_AMOUNT_SOURCES,
  navigationRules,
} from "@/b2b/config/partnerJourney";
import Header from "../components/Header";
import Div100vh from "react-div-100vh";
import PrimaryButton from "../components/PrimaryButton";
import CustomDrawer from "@/components/common/drawer";
import { Info } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import TextField from "@/components/common/Input/TextField";
import { debounce } from "lodash";
import { Separator } from "@radix-ui/react-select";
import { Shield } from "lucide-react";
import config from "@/config";
import moment from "moment";
import { SUPPORT_TEL_HREF } from "@/b2b/config/support";

const EXCLUDE_SD = ["tribe", "fabhostels", "youthville"];

const partnerRentRules = {
  housr: {
    includeGST: true,
    includePF: true,
    pfRate: 0.02,
    useNewLayout: true,
    pfType: "lease_based",
    pfRates: {
      3: 0.02,
      4: 0.02,
      5: 0.02,
      6: 0.02,
      7: 0.02,
      8: 0.02,
      9: 0.02,
      10: 0.02,
      11: 0.02,
    },
  },
  kots: {
    includeGST: false,
    includePF: true,
    useNewLayout: true,
    inputAddress: true,
    inputRoomInfo: true,
    inputBookingId: true,
    dynamicLeaseTenure: true,
    pfType: "lease_based",
    pfRates: {
      3: 0, 4: 0, 5: 0, 6: 0, 7: 0,
      8: 0, 9: 0, 10: 0, 11: 0,
    },
    includeOneTimeFee: true,
    oneTimeFee: 499,
    otherCharges: [
      {
        name: "Fixed Monthly Garbage Fees",
        value: 250,
      },
      {
        name: "Fixed Monthly Parking Charges",
        value: 1000,
      },
    ],
    getPfDisplayText: (rate) => `${(rate * 100).toFixed(0)}% of Monthly Rent`,
  },
  settl: {
    includeGST: true,
    includePF: true,
    pfRate: 0.02,
    useNewLayout: true,
  },
  xu: { includeGST: false, includePF: false, useNewLayout: false },
  hw: {
    includeGST: false,
    includePF: true,
    pfRate: 0.0225,
    useNewLayout: true,
  },
  meridianstays: {
    includeGST: false,
    includePF: true,
    pfRate: 0.025,
    includeOneTimeFee: true,
    oneTimeFee: 499,
    useNewLayout: true,
  },
  oxotel: {
    includeGST: false,
    includePF: true,
    pfRate: 0.025,
    includeOneTimeFee: true,
    oneTimeFee: 499,
    useNewLayout: true,
  },
  tribe: {
    includeGST: false,
    includePF: true,
    includeOneTimeFee: true,
    dynamicLeaseTenure: true,
    useNewLayout: true,
    pfType: "lease_based",
    pfRates: { 3: 0.035, 4: 0.045, 6: 0.075, 8: 0.085, 10: 0.11 },
    oneTimeFeeType: "lease_based",
    oneTimeFees: { 3: 499, 4: 599, 6: 999, 8: 2950, 10: 4720 },
    getPfDisplayText: (rate) => `${(rate * 100).toFixed(1)}% of Monthly Rent`,
  },
  nestaway: {
    includeGST: false,
    includePF: true,
    useNewLayout: true,
    pfType: "city_based",
    getPfRate: (city) =>
      ["bangalore", "mumbai", "pune"].includes(city?.toLowerCase() || "")
        ? 0.04
        : 0.03,
    getPfDisplayText: (rate, city) =>
      ["bangalore", "mumbai", "pune"].includes(city?.toLowerCase() || "")
        ? `4% of Monthly Rent`
        : "3% of Monthly Rent",
  },
  stanza: {
    includeGST: true,
    includePF: true,
    includeOneTimeFee: true,
    dynamicLeaseTenure: false,
    pfType: "type_based",
    oneTimeFee: 499,
    useNewLayout: true,
    pfRates: {
      student: {
        3: 0.02,
        6: 0.05,
        8: 0.07,
        10: 0.09,
      },
      salaried: {
        3: 0.02,
        6: 0.05,
        8: 0.07,
        10: 0.09,
      },
    },
    getPfDisplayText: (rate) => `${(rate * 100).toFixed(3)}% of Monthly Rent`,
  },
  friendzo: {
    includeGST: false,
    includePF: true,
    useNewLayout: true,
    includeOneTimeFee: true,
    dynamicLeaseTenure: false, //default
    pfType: "lease_based",
    oneTimeFee: 149,
    pfRates: {
      3: 0.02,
      4: 0.02,
      5: 0.03,
      6: 0.03,
      11: 0.04,
    },
    getPfDisplayText: (rate) =>
      `${parseFloat((rate * 100).toFixed(2))}% of the base rent`,
  },
  truliv: {
    includeGST: false,
    includePF: true,
    useNewLayout: true,
    pfType: "lease_based",
    pfRates: {
      3: 0.0225,
      6: 0.0225,
      9: 0.0225,
      11: 0.0225,
    },
  },
  fabhostels: {
    includeGST: false,
    includePF: true,
    useNewLayout: true,
    includeOneTimeFee: true,
    dynamicLeaseTenure: true,
    pfType: "lease_based",
    pfRates: {
      3: 0.02,
      6: 0.06,
      9: 0.09,
    },
    oneTimeFeeType: "lease_based",
    oneTimeFees: { 3: 999, 6: 999, 9: 1999 },
  },
  youthville: {
    includeGST: false,
    useNewLayout: true,
    includePF: false,
    includeOneTimeFee: true,
    dynamicLeaseTenure: true,
    oneTimeFeeType: "flat",
    oneTimeFee: 1999,
  },
  urbanroomz: {
    includeGST: false,
    includePF: true,
    useNewLayout: true,
    includeOneTimeFee: true,
    oneTimeFee: 499,
    dynamicLeaseTenure: true,
    pfType: "lease_based",
    pfRates: {
      2: 0.02, 3: 0.03, 4: 0.04, 5: 0.05,
      6: 0.07, 7: 0.08, 8: 0.09, 9: 0.10,
      10: 0.12, 11: 0.13,
    },
    getPfDisplayText: (rate) => `${(rate * 100).toFixed(0)}% of Monthly Rent`,
  },
  flent: {
    includeGST: false,
    includePF: true,
    useNewLayout: true,
    includeOneTimeFee: true,
    oneTimeFee: 499,
    dynamicLeaseTenure: true,
    pfType: "lease_based",
    pfRates: {
      2: 0.02, 3: 0.03, 4: 0.04, 5: 0.05,
      6: 0.07, 7: 0.08, 8: 0.09, 9: 0.10,
      10: 0.12, 11: 0.13,
    },
    getPfDisplayText: (rate) => `${(rate * 100).toFixed(0)}% of Monthly Rent`,
  },
  blossomstayz: {
    includeGST: false,
    includePF: false,
    useNewLayout: true,
    includeOneTimeFee: true,
    oneTimeFee: 199,
    dynamicLeaseTenure: false,
    hideBreakdown: true,
  },
  // Mirror of oho's PartnerRules row in the DB (cp-prod): platform fee is now
  // DISABLED (includePF:false) — oho charges no platform fee, only the ₹499 flat
  // onboarding fee, no GST, so finalRent = rent. The lease_based pfRates below are
  // kept dormant (never read while includePF is false) to mirror the DB row
  // exactly; revamp moves this to the DB API.
  oho: {
    includeGST: false,
    includePF: false,
    pfRate: 0.02,
    pfType: "lease_based",
    pfRates: {
      3: 0.03,
      4: 0.04,
      5: 0.05,
      6: 0.06,
      7: 0.07,
      8: 0.08,
      9: 0.09,
      10: 0.1,
      11: 0.11,
    },
    includeOneTimeFee: true,
    oneTimeFee: 499,
    oneTimeFeeType: "flat",
    dynamicLeaseTenure: true,
    useNewLayout: true,
    getPfDisplayText: (rate) => `${(rate * 100).toFixed(0)}% of Monthly Rent`,
  },
  // Backend-computed fees, no local breakdown shown. Deliberately has NO
  // includePF/pfRate/pfType/pfRates — that's what tells `computed` (below) to
  // trust the already-correct `formData.pf` the backend computed at lead
  // creation, instead of a locally-duplicated formula that would drift from
  // PartnerRules in the DB. `hideBreakdown` collapses the card to a single
  // total line (see `blossomstayz` above for the same pattern) — reusable for
  // any future partner that wants "one number, no breakdown" without needing
  // its fee formula ever hardcoded here.
  popcornstay: {
    useNewLayout: true,
    hideBreakdown: true,
    hideSecurityDeposit: true,
    finalLineLabel: "Monthly Payable",
  },
};

function AddressInputBar({ onSelect = () => {} }) {
  const partner = localStorage.getItem("partnerIdentifier");
  const userIdentifier = localStorage.getItem("user_identifier");

  const lastFetchedQuery = useRef("");

  const [query, setQuery] = useState("");
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState("");
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);

  const selectedProperty = properties.find((p) => p.id === selectedPropertyId);

  const fetchProperties = useCallback(
    async (searchQuery) => {
      if (
        !searchQuery ||
        searchQuery.length < 3 ||
        lastFetchedQuery.current === searchQuery
      ) {
        return;
      }

      lastFetchedQuery.current = searchQuery;

      try {
        setSearching(true);
        const fetched = await getDataB2B(
          `b2b/onboard/find-property?q=${searchQuery}`,
          userIdentifier
        );

        if (Array.isArray(fetched)) {
          setProperties(fetched);
          if (!fetched.some((p) => p.id === selectedPropertyId)) {
            setSelectedPropertyId("");
            onSelect(null);
          }
        } else {
          setProperties([]);
          setSelectedPropertyId("");
          onSelect(null);
        }
      } catch (err) {
        console.error("Failed to fetch properties:", err);
        setProperties([]);
        setSelectedPropertyId("");
        onSelect(null);
      } finally {
        setSearching(false);
      }
    },
    [userIdentifier, selectedPropertyId, onSelect]
  );

  const debouncedFetch = useMemo(
    () => debounce(fetchProperties, 500),
    [fetchProperties]
  );

  useEffect(() => {
    if (query.length >= 2) {
      debouncedFetch(query);
    } else {
      debouncedFetch.cancel();
      setProperties([]);
      setSelectedPropertyId("");
      onSelect(null);
      lastFetchedQuery.current = "";
      debouncedFetch();
    }

    return () => {
      debouncedFetch.cancel();
    };
  }, [query, debouncedFetch]);

  const handleSelect = (propertyId) => {
    const selected = properties.find((p) => p.id === propertyId);
    setSelectedPropertyId(propertyId);
    onSelect(selected || null);
    setOpen(false);
  };

  if (!partner) return null;

  return (
    <div className="relative w-full">
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="w-full text-left flex justify-between items-center hover:border-gray-400 transition text-xs"
      >
        <span>
          {selectedProperty ? selectedProperty.address : "Select property..."}
        </span>
        <span className="text-gray-400">▼</span>
      </button>

      {open && (
        <div className="absolute -top-0 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search property..."
            className="w-full px-3 py-2 border-b border-gray-100 focus:outline-none"
          />

          <Separator />

          <div className="max-h-52 overflow-y-auto">
            {properties.length === 0 ? (
              <div className="px-4 py-3 text-gray-500">
                {searching
                  ? "Searching..."
                  : query?.length === 0
                  ? "Start typing above..."
                  : query?.length > 3
                  ? "No properties found."
                  : "Try something else."}
              </div>
            ) : (
              properties.map((property) => (
                <div
                  key={property.id}
                  onClick={() => handleSelect(property.id)}
                  className={`px-4 py-2 cursor-pointer hover:bg-gray-100 flex items-start text-xs ${
                    property.id === selectedPropertyId
                      ? "bg-gray-100 font-medium"
                      : ""
                  }`}
                >
                  {property.address}
                  <div className="size-3">
                    {property.id === selectedPropertyId && (
                      <CheckCircle2Icon className="text-green-600 font-bold" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function RentSummary({ theme }) {
  const navigate = useNavigate();

  const [formData, setFormData] = useState(null);
  const [dataToSave, setUpdateData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isChecked, setIsChecked] = useState(true);
  const [error, setError] = useState("");
  const [showTooltip, setShowTooltip] = useState(false);
  const [drawerState, setDrawerState] = useState({ open: false, url: "" });
  const [journeyData, setJourneyData] = useState(null);
  // Null until the journey has said which lender it is: `next` records consents that name the
  // lender, so it must never record them against a guess (see journeyLenderCore.js).
  const lender = useJourneyLender(journeyData?.lender);

  const userIdentifier = localStorage.getItem("user_identifier");
  const partner = useMemo(() => localStorage.getItem("partnerIdentifier"), []);
  const rules = useMemo(() => partnerRentRules[partner] || {}, [partner]);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchData(`journey/full`);
        setJourneyData(data?.journey);
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  const computed = useMemo(() => {
    if (!formData) return {};

    const {
      tenure,
      existing_tenant,
      city = "",
      package_amount = 0,
      pf_rate: rawPfRate = 0,
      eligible_tenures = "",
    } = formData;
    const pf_rate = rawPfRate > 1 ? rawPfRate / 100 : rawPfRate;

    if (partner === "stanza") {
      if (formData?.isStudent || formData?.type === "student") {
        rules.dynamicLeaseTenure = true;
      }
    }

    console.log(formData, "formData insise computed");
    console.log(
      {
        tenure,
        existing_tenant,
        city,
        package_amount,
        pf_rate,
      },
      "extracted inside comnputed"
    );
    // base rent recalculates dynamically if package_amount present

    if (!tenure && eligible_tenures) {
      setFormData((prev) => {
        return {
          ...prev,
          tenure: Number(eligible_tenures?.split(",")?.[0]),
        };
      });
    }

    let baseRent =
      package_amount && rules?.dynamicLeaseTenure && tenure
        ? package_amount / tenure
        : formData.rent;

    // Partner's rule entry (if any) defines no fee formula of its own — either
    // there's no entry at all (a partner onboarded after this file was last
    // updated), or it deliberately omits pf fields (see `popcornstay` above)
    // to opt into the backend-authoritative path. Either way, the backend
    // already computed and stored the correct pf/sf/rent on the lead when it
    // was created (same math as b2b_dashboard's Rent Summary card and
    // createLead) — trust that instead of silently recomputing as if no
    // platform fee applies.
    const hasLocalFeeFormula = !!(
      rules.includePF ||
      rules.pfType ||
      rules.pfRate ||
      rules.pfRates
    );

    console.log(baseRent);
    // PF
    let pfRate = pf_rate || rules?.pf_rate || 0;
    console.log(pfRate, "pfRate inside");
    if (rules.includePF && !existing_tenant) {
      if (rules.pfType === "lease_based") {
        const leaseKey =
          partner === "housr" ? (formData?.lease ?? tenure + 1) : tenure;
        pfRate = rules.pfRates?.[leaseKey] ?? pf_rate ?? 0.02;
      } else if (rules.pfType === "type_based") {
        pfRate = rules.pfRates?.[formData?.type]?.[tenure] ?? pf_rate ?? 0.02;
      } else if (typeof rules.getPfRate === "function") {
        pfRate = rules.getPfRate(city, tenure);
      } else if (pf_rate) {
        pfRate = pf_rate;
      } else {
        pfRate = rules.pfRate ?? 0.02;
      }
    }
    let pfAmount = (partner === "housr" || !existing_tenant) ? Math.round(baseRent * pfRate) : 0;

    console.log(pfAmount, "pfAmount   inside");
    // GST
    const gstAmount =
      rules.includeGST && baseRent > 20000 ? Math.round(baseRent * 0.05) : 0;

    // other charges
    const otherCharges =
      rules?.otherCharges?.length > 0
        ? rules?.otherCharges?.reduce((sum, item) => sum + (item.value || 0), 0)
        : 0;

    // One‑time fee
    let oneTimeFee = 0;
    if (rules.includeOneTimeFee) {
      if (rules.oneTimeFeeType === "lease_based") {
        oneTimeFee = rules.oneTimeFees?.[tenure] ?? 0;
      } else {
        oneTimeFee = rules.oneTimeFee ?? 0;
      }
    }

    let finalObjToReturn;

    if (partner !== "kots") {
      pfAmount = (partner === "housr" || !existing_tenant)
        ? Math.round((baseRent + (gstAmount || 0)) * pfRate)
        : 0;
      if (!hasLocalFeeFormula && formData?.pf != null && !existing_tenant) {
        pfAmount = formData.pf;
        // Keep the displayed rate text consistent with the amount above —
        // derive it from the same authoritative pf/baseRent instead of
        // leaving the unrelated local pfRate (0, since this partner has no
        // local rule) next to a non-zero amount.
        pfRate = baseRent > 0 ? pfAmount / baseRent : 0;
      }
      return {
        baseRent,
        pfRate,
        pfAmount,
        gstAmount,
        oneTimeFee,
        finalRent: Math.round(baseRent + pfAmount + gstAmount + otherCharges),
      };
    } else {
      baseRent = formData?.rent || 0;
      pfAmount = !existing_tenant ? Math.round(baseRent * pfRate) : 0;
      return {
        baseRent,
        pfRate,
        pfAmount,
        gstAmount,
        oneTimeFee,
        finalRent: Math.round(baseRent + pfAmount + gstAmount),
      };
    }

    // console.log(finalObjToReturn)
    // return finalObjToReturn;
  }, [formData, rules, journeyData]);

  useEffect(() => {
    console.log(formData, "formData");
    if (formData) return;
    (async () => {
      try {
        const token = localStorage.getItem("user_identifier");
        const { user, token: signupToken } = await getDataB2B(
          "b2b/onboard/start",
          token
        );
        console.log(user, "use");
        setJourneyToken(signupToken);
        setFormData(user);
      } catch (err) {
        console.error(err);
      }
    })();
  }, [formData]);

  const onSelectProperty = (property) => {
    if (property) {
      setFormData((prev) => {
        return {
          ...prev,
          property_id: property.id,
          property_address: property?.address,
          city: property?.city,
        };
      });
    }
  };

  const toggleCheckbox = () => setIsChecked((p) => !p);

  const changeTenure = useCallback((value) => {
    setFormData((prev) => {
      const newTenure = Number(value);
      const newRent = prev.package_amount
        ? prev.package_amount / newTenure
        : prev.rent;
      return { ...prev, tenure: newTenure, rent: newRent };
    });
    console.log(formData, "formData");
  }, []);

  const openDrawer = (url) => setDrawerState({ open: true, url });
  const closeDrawer = () => setDrawerState({ open: false, url: "" });

  const next = async () => {
    if (!isChecked) return setError("Please tick the checkbox to continue.");
    if (!lender) {
      return setError("Still loading your application details. Please tap Next again in a moment.");
    }
    if (rules?.inputRoomInfo && !formData?.flat_number?.trim()) {
      return setError("Please enter the flat / room number to continue.");
    }
    if (
      rules?.inputBookingId &&
      !(formData?.ext_identifier || formData?.uid)?.trim()
    ) {
      return setError("Please enter the booking ID to continue.");
    }
    setError("");
    setIsLoading(true);
    // consent tracking b2b

    const platform =
      navigator?.userAgentData?.platform ||
      navigator.platform ||
      localStorage.getItem("UTM_SOURCE");

    Promise.allSettled([
      trackConsent(
        "Checkbox",
        "Current Address",
        "",
        platform,
        CONSENT_MESSAGES.ADDRESS
      ),
      trackConsent(
        "Checkbox",
        lender.legalName,
        "",
        platform,
        lender.consents.apply
      ),
      trackConsent("Checkbox", "CKYC", "", platform, lender.consents.ckyc),
    ]).catch((error) => console.log("Unbale to recon."));

    try {
      await updateFunction({ acceptedRentSummary: true }, "user/update");
      if (rules?.dynamicLeaseTenure || rules?.inputAddress) {
        const res = await updateFunctionB2B(
          formData,
          "b2b/onboard/confirm-rent-summary",
          "confirm-rent-summary",
          "confirm-rent-summary",
          userIdentifier
        );
      }

      // KOTS skips the maintenance cover screen entirely (no maintenance for
      // KOTS), so it never routes into "maintenance-details".
      const nextStep =
        partner === "housr"
          ? "tenure-details"
          : journeyData?.maintenanceAmount && partner !== "kots"
            ? "maintenance-details"
            : partnerNavigationRules?.[partner]?.rentSummary || "/onboarding";
      navigate(`/b/${userIdentifier}?step=${nextStep}&partner=${partner}`);
    } finally {
      setIsLoading(false);
    }
  };

  const maintenance = useMemo(
    () =>
      getMaintenanceInfo(partner, formData?.city, computed.baseRent || 0) || {
        amount: 0,
        tooltip: "",
      },
    [partner, formData, computed?.baseRent]
  );
  const isLockInPartner = partner === "housr";

  const lockIn = useMemo(() => {
    // `lease` is the fallback for leads written before the backend started recording
    // lockInPeriod — under that older logic `lease` IS the number the resident picked
    // (lease = pick, tenure = pick - 1). Once lockInPeriod is being written it wins, and
    // `lease` becomes the fixed 6, so the order here matters.
    const months =
      formData?.lockInPeriod ??
      journeyData?.lockInPeriod ??
      formData?.lease ??
      formData?.tenure ??
      null;

    const endRaw = formData?.lockInEndDate ?? journeyData?.lockInEndDate ?? null;
    const start = formData?.movein_date || journeyData?.moveInDate;

    return {
      months,
      // Derive the expiry locally whenever the stored value is absent, so the screen
      // shows a real date rather than a blank.
      end: endRaw
        ? formatDate(endRaw)
        : months && start
          ? moment(start)
              .add(months - 1, "months")
              .endOf("month")
              .format("DD-MM-YYYY")
          : null,
    };
  }, [formData, journeyData]);

  console.log(rules, "rules");
  console.log(computed, "computed");

  const dynamicLeaseTenure = rules.dynamicLeaseTenure;
  const isExistingTenant = formData?.existing_tenant;

  console.log(rules.includePF, formData?.pf_rate, rules.pfRate);
  return (
    <Div100vh
      className={`bg-[${theme?.bgColor}] ${theme?.textColor} pt-8 px-8 sm:pb-8 flexs flex-col justify-between overflow-scroll`}
    >
      <div>
        <Header
          heading={`${
            dynamicLeaseTenure || formData?.package_amount
              ? "Repayment"
              : "Rent"
          } Summary`}
        />

        {partner !== "kots" && !rules.hideSecurityDeposit && <div
          className="rounded-xl p-4 my-4 flex items-center justify-between shadow-sm"
          style={{
            background:
              theme?.sdBg || "linear-gradient(135deg, #E8F7EA, #F3FFF4)",
          }}
        >
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-white/60 p-2 shadow-sm flex items-center justify-center">
              <Shield size={20} className="text-green-600" />
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-[1rem] font-semibold text-[#595959]">
                Security Deposit
              </span>
            </div>
          </div>

          <div className="flex flex-col items-end">
            <span className="text-green-500 text-lg">₹ 0/-</span>
            <span className="text-gray-400 line-through text-xs">
              {formData?.sd
                ? formatINR(formData?.sd)
                : formatINR(computed.baseRent * 2)}
              /-
            </span>
          </div>
        </div>}

        {/* fees card */}
        <div
          className={`${theme?.tint} rounded-xl border ${
            rules.useNewLayout ? "border-gray-200" : "divide-y border-blue-200"
          } p-4 sm:p-6 my-4 flex flex-col ${
            rules.useNewLayout ? "gap-3" : "gap-2"
          }`}
        >
          {rules.useNewLayout ? (
            <>
              {!rules.hideBreakdown && (
                <h3 className="text-base font-semibold text-[#333] mb-1">
                  Monthly Rent Breakup
                </h3>
              )}

              <div className="space-y-3">
                {!rules.hideBreakdown && partner !== "kots" && (
                  <div className="flex justify-between items-start">
                    <div className="flex flex-col">
                      <span className="text-[#222] font-medium text-sm">
                        Base rent
                      </span>
                      <span className="text-xs text-gray-500">
                        original rent of the property
                      </span>
                    </div>
                    <span className={`${theme?.textColor} font-medium text-sm`}>
                      {formatINR(computed.baseRent)}/-
                    </span>
                  </div>
                )}

                {!rules.hideBreakdown && partner !== "kots" && rules.includeGST && computed.baseRent > 20000 && (
                  <div className="flex justify-between items-start">
                    <div className="flex flex-col">
                      <span className="text-[#222] font-medium text-sm">
                        GST
                      </span>
                      <span className="text-xs text-gray-400">
                        5% of the base rent amount
                      </span>
                    </div>
                    <span className={`${theme?.textColor} font-medium text-sm`}>
                      {formatINR(computed.gstAmount)}/-
                    </span>
                  </div>
                )}

                {!rules.hideBreakdown && partner !== "kots" && (rules.includePF || computed.pfAmount) ? (
                  <div className="flex justify-between items-start">
                    <div className="flex flex-col">
                      <span className="text-[#222] font-medium text-sm flex items-center gap-1">
                        Platform fees
                        {/* <Info className="h-3.5 w-3.5 text-blue-500" />*/}
                      </span>
                      <span className="text-xs text-gray-500">
                        {rules.getPfDisplayText
                          ? rules.getPfDisplayText(
                              computed.pfRate,
                              formData?.city
                            )
                          : `${parseFloat((computed.pfRate * 100).toFixed(2))}% of the base rent + GST`}
                      </span>
                    </div>
                    <span
                      className={`${theme?.textColor} font-medium text-sm ${
                        isExistingTenant && "line-through"
                      }`}
                    >
                      {formatINR(computed.pfAmount)}/-
                      {isExistingTenant && (
                        <span className="text-green-500 ml-2">₹ 0/-</span>
                      )}
                    </span>
                  </div>
                ) : null}

                {!rules.hideBreakdown && partner !== "kots" && rules.otherCharges && rules.otherCharges.length > 0 && (
                  <>
                    {rules.otherCharges.map((charge, index) => (
                      <div
                        key={index}
                        className="flex justify-between items-start"
                      >
                        <div className="flex flex-col">
                          <span className="text-[#222] font-medium text-sm">
                            {charge.name}
                          </span>
                        </div>
                        <span
                          className={`${theme?.textColor} font-medium text-sm`}
                        >
                          {formatINR(charge?.value)}/-
                        </span>
                      </div>
                    ))}
                  </>
                )}

                <div className="border-t border-gray-200 pt-3 flex justify-between items-start">
                  <div className="flex flex-col">
                    <span className="text-[#222] font-medium text-base">
                      {rules.finalLineLabel || "Final Monthly rent"}
                    </span>
                    <span className="text-xs text-gray-500">
                      auto deducted monthly
                    </span>
                  </div>
                  <span className={`${theme?.textColor} font-medium text-base`}>
                    {formatINR(computed.finalRent)}/-
                  </span>
                </div>

              </div>
            </>
          ) : (
            <>
              <div className="space-y-1 text-[#999] py-2">
                <div className="flex justify-between text-[#222] font-medium text-sm">
                  <div className="flex flex-col">
                    Monthly {dynamicLeaseTenure ? "Repayment" : "Rent"}
                    <span className="text-2xs opacity-65">
                      {partner === "truliv" ? "(all inclusive)" : ""}
                    </span>
                  </div>
                  <span className={theme?.textColor}>
                    {formatINR(computed.baseRent)}/-
                  </span>
                </div>
              </div>

              {rules.includeGST && computed.baseRent > 20000 && (
                <div className="flex justify-between items-start py-2">
                  <div className="flex flex-col">
                    <span className="text-[#222] font-medium text-sm">GST</span>
                    <span className="text-xs text-gray-400">
                      5% of the base rent amount
                    </span>
                  </div>
                  <span className={`${theme?.textColor} font-medium text-sm`}>
                    {formatINR(computed.gstAmount)}/-
                  </span>
                </div>
              )}
              {/* {!EXCLUDE_SD.includes(partner) && !rules.useNewLayout && (
                <div className="space-y-1 text-[#999] py-2">
                  <div className="flex justify-between text-[#0031fb] font-medium text-sm">
                    <div className="space-y-1">
                      <div>Security Deposit</div>
                      <div className="flex gap-1 text-xs">
                        <span className="text-black/60">powered by</span>
                        <span className="text-black/60 flex gap-[0.5px] not-italic">
                          Circle <span className="text-blue-600">Pe</span>
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end">
                      {partner !== "fabhostels" ? (
                        <span className="text-green-500 text-lg">₹ 0/-</span>
                      ) : (
                        <span className="text-black-light">₹ 0/-</span>
                      )}
                      <span className="text-gray-400 line-through text-xs">
                        {formData?.sd
                          ? formatINR(formData?.sd)
                          : formatINR(computed.baseRent * 2)}
                        /-
                      </span>{" "}
                    </div>
                  </div>
                </div>
              )} */}

              {rules.includePF || computed.pfAmount ? (
                <div className="space-y-1 text-[#999] py-2">
                  <div className="flex justify-between text-[#222] font-medium text-sm">
                    Platform Fee
                    <span>
                      <span
                        className={`${theme?.textColor} ${
                          isExistingTenant && "line-through"
                        }`}
                      >
                        ₹ {computed.pfAmount}/-
                      </span>
                      {isExistingTenant && (
                        <span className="text-green-500"> ₹ 0/-</span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs sm:text-2xs">
                    {rules.getPfDisplayText
                      ? rules.getPfDisplayText(computed.pfRate, formData?.city)
                      : `${parseFloat((computed.pfRate * 100).toFixed(2))}% of Final Monthly Rent`}
                  </div>
                </div>
              ) : null}

              {rules.otherCharges && rules.otherCharges.length > 0 && (
                <div className="space-y-2 py-2">
                  {rules.otherCharges.map((charge, index) => (
                    <div
                      key={index}
                      className="flex justify-between text-[#999] text-xs"
                    >
                      {charge.name}
                      <span className={theme?.textColor}>
                        {formatINR(charge?.value)}/-
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className="border-y py-4 space-y-1 text-[#999]">
                <div className="flex justify-between text-black font-medium text-base sm:text-md">
                  Total {dynamicLeaseTenure ? "Amount" : "Rent"}
                  {partner !== "stanza" || partner !== "fabhostels" ? (
                    <span className={theme?.textColor}>
                      {formatINR(computed.finalRent)}/-
                    </span>
                  ) : (
                    <span className="text-black-light">
                      {formatINR(computed.finalRent)}/-
                    </span>
                  )}
                </div>
              </div>

              {/* {computed.oneTimeFee > 0 && (
                <div className="space-y-1">
                  <div className="flex justify-between text-[#999] font-medium text-xs p-1">
                    One‑time CirclePe Onboarding Fee
                    <span className={theme?.textColor}>
                      {formData?.sf
                        ? formatINR(formData?.sf)
                        : formatINR(computed.oneTimeFee)}
                      /-
                    </span>
                  </div>
                </div>
              )} */}

              {formData?.existing_tenant && partner === "housr" && (
                <div className="w-full text-right text-3xs leading-3 ">
                  <div>
                    One time {formatINR(500)} CirclePe Onboarding Fee applicable.
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {partner !== "kots" && (computed?.oneTimeFee ?? formData?.sf ?? rules?.oneTimeFee) > 0 && (
          <div
            className={`rounded-xl border p-4 my-4 flex items-center justify-between shadow-sm ${
              theme?.onboardingBg || "bg-yellow-50"
            }`}
            role="region"
            aria-label="CirclePe Onboarding Fee"
          >
            <div className="flex flex-col">
              <div className="text-sm font-medium text-[#222]">
                CirclePe Onboarding Fee
              </div>
              <div className="text-xs text-gray-600">One-time charge</div>
            </div>

            <div className="text-right">
              <div className={`${theme?.textColor} text-lg font-semibold`}>
                {formatINR(
                  formData?.sf ?? computed?.oneTimeFee ?? rules?.oneTimeFee ?? 0
                )}
                /-
              </div>
            </div>
          </div>
        )}

        <div className="rounded-xl border p-4 grid grid-cols-3 gap-4">
          <div className="flex flex-col flex-1 col-span-2">
            <span className="text-[#484848] font-[600] text-xs">
              Property Address
            </span>
            <span className={`${theme?.textColor} text-xs text-wrap`}>
              {journeyData?.property?.address || formData?.property_address || ""}
            </span>
          </div>

          <div className="flex flex-col flex-1">
            <span className="text-[#484848] font-[600] text-xs mb-1">
              {isLockInPartner ? "Lock-in period" : "Lease Tenure"}
            </span>
            <span className={`${theme?.textColor} text-xs`}>
              {isLockInPartner
                ? lockIn.months
                : formData?.lease ||
                  formData?.tenure ||
                  journeyData?.loanTenure + 1}{" "}
              months
            </span>
          </div>

          <div className="col-span-3 flex gap-6 py-2">
            <div className="flex flex-col flex-1">
              <span className="text-[#484848] font-[600] text-xs mb-1 min-w-max">
                {isLockInPartner ? "Lock-in start date" : "Lease start"}
              </span>
              <span className={`${theme?.textColor} text-xs`}>
                {formatDate(formData?.movein_date || journeyData?.moveInDate)}
              </span>
            </div>

            <div className="flex flex-col flex-1">
              <span className="text-[#484848] font-[600] text-xs mb-1 min-w-max">
                {isLockInPartner ? "Lock-in end date" : "Lease end"}
              </span>
              <span className={`${theme?.textColor} text-xs`}>
                {isLockInPartner
                  ? lockIn.end
                  : moment(formData?.movein_date || journeyData?.moveInDate)
                      .add(
                        formData?.lease || journeyData?.loanTenure + 1,
                        "months"
                      )
                      .subtract(1, "day")
                      .format("DD-MM-YYYY")}
              </span>
            </div>
          </div>

          {/* {ADD_MAINTENANCE_AMOUNT_SOURCES.includes(partner?.toLowerCase()) && (
            <div className="flex justify-between py-2">
              <span className="text-[#999] font-medium text-xs">
                Security deposit
              </span>
              <span
                className={`${theme?.textColor} text-xs flex items-center gap-2`}
              >
                {maintenance.amount
                  ? `₹ ${maintenance.amount.toLocaleString("en-IN")}`
                  : "NA"}
                {maintenance.tooltip && (
                  <div
                    className="relative cursor-pointer"
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                  >
                    <Info className="h-4 w-4 text-gray-500 hover:text-gray-700" />
                    {showTooltip && (
                      <div className="absolute left-1/2 bottom-full mb-2 -translate-x-1/2 w-[250px] bg-gray-800 text-white text-xs p-2 rounded-md shadow-lg">
                        {maintenance.tooltip}
                      </div>
                    )}
                  </div>
                )}
              </span>
            </div>
          )}*/}

          {/* {partnerData?.[theme?.name]?.noticePeriod && (
            <div className="flex justify-between py-2">
              <span className="text-[#999] font-medium text-xs">
                Notice Period
              </span>
              <span className={`${theme?.textColor} text-xs`}>
                {partnerData?.[theme?.name]?.noticePeriod} days
              </span>
            </div>
          )}*/}

          {/* {!formData?.property_address && (
            <div className="flex flex-col p-1 gap-1">
              <div className="text-[#999] font-medium text-xs">
                Property Address
              </div>
              <AddressInputBar onSelect={onSelectProperty} />
            </div>
          )}*/}

          {rules?.inputRoomInfo && (
            <div className="flex flex-col col-span-3 gap-1">
              <span className="text-[#484848] font-[600] text-xs">
                Flat Number
              </span>
              <input
                type="text"
                value={formData?.flat_number || ""}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, flat_number: e.target.value }))
                }
                placeholder="Enter flat / room number"
                className="border border-gray-300 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-gray-500 w-full"
              />
            </div>
          )}

          {rules?.inputBookingId && (
            <div className="flex flex-col col-span-3 gap-1">
              <span className="text-[#484848] font-[600] text-xs">
                Booking ID
              </span>
              <input
                type="text"
                value={formData?.ext_identifier || formData?.uid || ""}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, uid: e.target.value }))
                }
                placeholder="Enter booking ID"
                className="border border-gray-300 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-gray-500 w-full"
              />
            </div>
          )}
        </div>
      </div>

      <CustomDrawer isOpen={drawerState.open}>
        <div className="h-full w-full absolute inset-0">
          <iframe
            src={drawerState.url}
            title="Terms & Conditions"
            className="w-full h-full rounded-xl border border-[#444] my-4"
            allowFullScreen
          />
        </div>
        <button
          onClick={closeDrawer}
          className="absolute top-8 left-8 p-2 bg-white rounded-full hover:bg-slate-100 flex justify-center items-center"
        >
          <ChevronLeftIcon className="size-112 text-black" />
        </button>
      </CustomDrawer>

      {journeyData?.status === "LOCKED" && partner !== "kots" ? (
        <div
          className={`${theme?.tint} rounded-xl border p-4 sm:p-6 my-4 flex flex-col gap-4`}
        >
          <span className={`text-center text-sm ${theme?.textColor}`}>
            Contact <span className="font-bold">{partner?.toUpperCase()}</span>{" "}
            team for further steps
          </span>
          <PrimaryButton
            text="Call CirclePe Agent"
            handleClick={() => (window.location.href = SUPPORT_TEL_HREF)}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-4 py-2">
          {[
            // {
            //   id: "rent-financing-consent",
            //   label: (
            //     <>
            //       I hereby apply the RENT FINANCING with Fexprime Finance Pvt.
            //       Ltd. ("FFPL"/ "Lender") as per the details provided by me and
            //       I confirm to have read, understood and agreed to the{" "}
            //       <a
            //         href="https://www.fexprime.com/Personal-LoanRent-Financing"
            //         target="_blank"
            //         rel="noreferrer"
            //         className="underline text-blue-600"
            //       >
            //         PRODUCT DETAILS
            //       </a>{" "}
            //       and other relevant{" "}
            //       <span
            //         className="underline text-blue-600 cursor-pointer"
            //         onClick={() =>
            //           openDrawer(
            //             "https://circleapp-docs.s3.us-east-1.amazonaws.com/2.+APPLICATION+TERMS+-+PLCP+-+MOBILE+JOURNEY.pdf"
            //           )
            //         }
            //       >
            //         APPLICATION TERMS
            //       </span>
            //     </>
            //   ),
            // },
            // {
            //   id: "ckyc-consent",
            //   label: (
            //     <>
            //       I hereby authorize and give my consent to FFPL to download my
            //       KYC records from the Central KYC Registry (CKYCR) for the
            //       purpose of verification of my identity and address from the
            //       CKYCR database.
            //     </>
            //   ),
            // },
            {
              id: "address-consent",
              label: <>{CONSENT_MESSAGES.ADDRESS}</>,
            },
          ].map(({ id, label }) => (
            <div
              key={id}
              className="flex items-start gap-3 text-gray-400 text-xs"
            >
              <div
                className={`w-4 h-4 mt-0.5 border-2 flex items-center justify-center cursor-pointer ${
                  isChecked ? "bg-[#eee]" : "border-gray-400"
                }`}
              >
                <input
                  type="checkbox"
                  id={id}
                  checked={isChecked}
                  onChange={() => setIsChecked(!isChecked)}
                />
              </div>
              <label htmlFor={id} className="leading-snug text-3xs">
                {label}
              </label>
            </div>
          ))}

          {error && (
            <span className="text-red-500 text-2xs">{error}</span>
          )}

          <PrimaryButton
            text="Next"
            handleClick={next}
            className={theme.primary}
            loading={isLoading}
          />
          {lender && (
            <div className="flex items-center justify-center">
              <span className="text-black text-2xs italic">powered by</span>
              <img
                src={lender.logoUrl}
                alt={lender.legalName}
                className="inline-block max-h-4 ml-1"
              />
            </div>
          )}
        </div>
      )}
    </Div100vh>
  );
}
